import {
  DEFAULT_NOTE_STYLE,
  type NoteTextRun,
  type NoteTextStyle,
} from '../domain/classroom-note-share';
import { studyNoteUsesColumns, type StudyNoteSection } from '../domain/classroom-study-note';
import { STUDY_NOTE_DECOR, studyNoteTone } from './classroom-note-artwork';

export const NOTE_IMAGE_WIDTH = 1080;
export const NOTE_IMAGE_MAX_HEIGHT = 2560;
const LEFT = 50;
const CONTENT_WIDTH = NOTE_IMAGE_WIDTH - LEFT * 2;
const TITLE_WIDTH = 680;
const BODY_X = LEFT + 100;
const BODY_WIDTH = NOTE_IMAGE_WIDTH - LEFT - BODY_X;
const FOOTER_HEIGHT = 248;
const SECTION_GAP = 44;
const FONT_FAMILY = '"Yuanti SC", "PingFang SC", "Microsoft YaHei", system-ui, sans-serif';
const INK = '#352B25';

type ArtworkKey = keyof typeof STUDY_NOTE_DECOR;
export type NoteImageArtwork = Readonly<Record<ArtworkKey, HTMLImageElement>>;
interface TextPiece {
  readonly text: string;
  readonly width: number;
  readonly style: NoteTextStyle;
}
export interface NoteImageLine {
  readonly pieces: readonly TextPiece[];
  readonly height: number;
  readonly fontSize: number;
  readonly bullet?: boolean;
}
export interface NoteImagePanel {
  readonly section: number;
  readonly continued: boolean;
  readonly lines: readonly NoteImageLine[];
  readonly height: number;
  readonly headingLines: readonly NoteImageLine[];
  readonly headingHeight: number;
  readonly columns: boolean;
}
export interface NoteImagePage {
  readonly panels: readonly NoteImagePanel[];
  readonly height: number;
  readonly bodyTop: number;
  readonly titleLines: readonly NoteImageLine[];
  readonly metadataLines: readonly NoteImageLine[];
}
export interface NoteImageMetadata {
  readonly title: string;
  readonly author?: string;
  readonly date?: string;
}

export function noteImageFontSize(style: NoteTextStyle): number {
  return Math.round(Math.min(32, Math.max(13, style.fontSize)) * 2);
}
function noteFont(style: NoteTextStyle): string {
  return `${style.italic ? 'italic ' : ''}${style.bold ? '800' : '400'} ${noteImageFontSize(style)}px ${FONT_FAMILY}`;
}

/** Wrap by grapheme, retaining every character and avoiding CJK/emoji overflow. */
export function wrapNoteParagraph(
  runs: readonly NoteTextRun[],
  measure: (text: string, style: NoteTextStyle) => number,
  maxWidth = CONTENT_WIDTH,
): readonly NoteImageLine[] {
  const lines: NoteImageLine[] = [];
  let pieces: TextPiece[] = [];
  let width = 0;
  let fontSize = 0;
  const flush = () => {
    if (!pieces.length) return;
    lines.push({ pieces, height: Math.ceil(fontSize * 1.75), fontSize });
    pieces = [];
    width = 0;
    fontSize = 0;
  };
  const segmenter = new Intl.Segmenter('zh', { granularity: 'grapheme' });
  for (const run of runs) {
    for (const { segment } of segmenter.segment(run.text)) {
      const size = measure(segment, run);
      if (width + size > maxWidth && pieces.length) flush();
      pieces.push({ text: segment, width: size, style: run });
      width += size;
      fontSize = Math.max(fontSize, noteImageFontSize(run));
    }
  }
  flush();
  return lines;
}

export function layoutNoteTitle(
  title: string,
  measure: (text: string, style: NoteTextStyle) => number,
): readonly NoteImageLine[] {
  return wrapNoteParagraph(
    [
      {
        ...DEFAULT_NOTE_STYLE,
        color: INK,
        fontSize: 28,
        bold: true,
        text: title.trim() || '课堂笔记',
      },
    ],
    measure,
    TITLE_WIDTH,
  ).map((line) => ({ ...line, height: Math.ceil(line.fontSize * 1.45) }));
}

/** One ruled sheet. Repeated headings make PNG continuation pages understandable. */
export function layoutNoteImages(
  sections: readonly StudyNoteSection[],
  measure: (text: string, style: NoteTextStyle) => number,
  metadata: NoteImageMetadata = { title: '课堂笔记' },
): readonly NoteImagePage[] {
  const titleLines = layoutNoteTitle(metadata.title, measure);
  const meta = [metadata.author, metadata.date].filter(Boolean).join(' · ');
  const metadataLines = meta
    ? wrapNoteParagraph(
        [{ ...DEFAULT_NOTE_STYLE, fontSize: 13, color: '#796c5d', text: meta }],
        measure,
      )
    : [];
  const bodyTop =
    Math.max(272, 168 + titleLines.reduce((sum, line) => sum + line.height, 0)) +
    metadataLines.reduce((sum, line) => sum + line.height, 0);
  if (bodyTop > NOTE_IMAGE_MAX_HEIGHT / 2)
    throw new Error('便签标题过长，请缩短标题；正文内容不会删减。');
  const pages: NoteImagePage[] = [];
  let panels: NoteImagePanel[] = [];
  let used = bodyTop;
  const finishPage = () => {
    if (!panels.length) return;
    const last = panels.at(-1)!;
    const footerOverlap =
      !last.columns &&
      last.lines.length >= 2 &&
      last.lines
        .slice(-2)
        .every((line) => line.pieces.reduce((sum, piece) => sum + piece.width, 0) <= 440)
        ? 116
        : 0;
    pages.push({
      panels,
      height: used - SECTION_GAP + FOOTER_HEIGHT - footerOverlap,
      bodyTop,
      titleLines,
      metadataLines,
    });
    panels = [];
    used = bodyTop;
  };
  sections.forEach((section, index) => {
    const heading = section.heading;
    const headingLines = heading
      ? wrapNoteParagraph(
          [{ ...DEFAULT_NOTE_STYLE, text: heading, color: '#385339', fontSize: 24, bold: true }],
          measure,
          CONTENT_WIDTH - 112,
        ).map((line) => ({ ...line, height: Math.ceil(line.fontSize * 1.5) }))
      : [];
    const headingHeight = Math.max(
      72,
      headingLines.reduce((sum, line) => sum + line.height, 0),
    );
    const columns =
      studyNoteUsesColumns(section) &&
      section.items.every(
        (text) =>
          measure(text, { ...DEFAULT_NOTE_STYLE, fontSize: 22 }) <=
          CONTENT_WIDTH / section.items.length - 100,
      );
    const sectionLines = section.items.flatMap((text) =>
      wrapNoteParagraph(
        [{ ...DEFAULT_NOTE_STYLE, fontSize: columns ? 22 : 20, color: INK, text }],
        measure,
        columns ? CONTENT_WIDTH / section.items.length - 100 : BODY_WIDTH,
      ).map((line, i, lines) => ({
        ...line,
        bullet: i === 0,
        height: line.height + (i === lines.length - 1 ? 6 : 0),
      })),
    );
    // Only use columns if font metrics confirm that every label fits its own cell.
    const inline = columns && sectionLines.length === section.items.length;
    const bodyHeight = inline
      ? Math.max(0, ...sectionLines.map((line) => line.height))
      : sectionLines.reduce((sum, line) => sum + line.height, 0);
    const sectionHeight = headingHeight + bodyHeight;
    const available = NOTE_IMAGE_MAX_HEIGHT - bodyTop - FOOTER_HEIGHT;
    if (headingHeight + Math.max(0, ...sectionLines.map((line) => line.height)) > available)
      throw new Error('便签分组标题过长，请缩短分组标题；正文内容不会删减。');
    if (
      used > bodyTop &&
      sectionHeight <= available &&
      used + sectionHeight + FOOTER_HEIGHT > NOTE_IMAGE_MAX_HEIGHT
    )
      finishPage();
    let lines: NoteImageLine[] = [];
    let height = 0;
    let continued = false;
    const finishPanel = () => {
      if (!lines.length && !headingLines.length) return;
      const panelHeight = headingHeight + height;
      panels.push({
        section: index + 1,
        continued,
        lines,
        height: panelHeight,
        headingLines,
        headingHeight,
        columns: inline,
      });
      used += panelHeight + SECTION_GAP;
      lines = [];
      height = 0;
      continued = true;
    };
    if (inline) {
      lines = sectionLines;
      height = bodyHeight;
    } else {
      for (const line of sectionLines) {
        if (used + headingHeight + height + line.height + FOOTER_HEIGHT > NOTE_IMAGE_MAX_HEIGHT) {
          // Do not leave an orphan heading when the first body line must move to the next page.
          if (lines.length) finishPanel();
          finishPage();
        }
        lines.push(line);
        height += line.height;
      }
    }
    finishPanel();
  });
  finishPage();
  return pages;
}

/** All local artwork is ready before the download control becomes available. */
export async function loadNoteArtwork(): Promise<NoteImageArtwork> {
  const load = (url: string) =>
    new Promise<HTMLImageElement>((resolve, reject) => {
      const image = new Image();
      const timeout = window.setTimeout(
        () => reject(new Error('便签素材加载超时，请重试。')),
        15_000,
      );
      image.onload = () => {
        window.clearTimeout(timeout);
        if (image.naturalWidth && image.naturalHeight) resolve(image);
        else reject(new Error('便签素材加载失败，请重试。'));
      };
      image.onerror = () => {
        window.clearTimeout(timeout);
        reject(new Error('便签素材加载失败，请重试。'));
      };
      image.src = url;
    });
  const entries = await Promise.all(
    Object.entries(STUDY_NOTE_DECOR).map(async ([key, url]) => [key, await load(url)] as const),
  );
  return Object.fromEntries(entries) as NoteImageArtwork;
}

export function noteCanvasMeasure(
  context: CanvasRenderingContext2D,
): (text: string, style: NoteTextStyle) => number {
  return (text, style) => {
    context.font = noteFont(style);
    return context.measureText(text).width;
  };
}

function drawLines(
  context: CanvasRenderingContext2D,
  lines: readonly NoteImageLine[],
  y: number,
  left = LEFT,
) {
  for (const line of lines) {
    let x = left;
    if (line.bullet) {
      context.fillStyle = '#61765B';
      context.font = `400 34px ${FONT_FAMILY}`;
      context.fillText('•', left - 44, y + (line.height - line.fontSize) / 2);
    }
    for (const piece of line.pieces) {
      context.font = noteFont(piece.style);
      context.fillStyle = piece.style.color;
      context.fillText(piece.text, x, y + (line.height - line.fontSize) / 2);
      x += piece.width;
    }
    y += line.height;
  }
  return y;
}

export function renderNoteImage(
  canvas: HTMLCanvasElement,
  page: NoteImagePage,
  _metadata: NoteImageMetadata,
  artwork: NoteImageArtwork,
  pageNumber: number,
  pageCount: number,
): void {
  canvas.width = NOTE_IMAGE_WIDTH;
  canvas.height = page.height;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('当前浏览器不支持生成便签图片。');
  context.fillStyle = '#FCF8EF';
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.drawImage(
    artwork.paper,
    0,
    0,
    canvas.width,
    (canvas.width * artwork.paper.naturalHeight) / artwork.paper.naturalWidth,
  );
  context.strokeStyle = '#D9C6A9';
  context.lineWidth = 2;
  context.strokeRect(6, 6, canvas.width - 12, canvas.height - 12);
  context.textBaseline = 'top';
  const contain = (
    image: HTMLImageElement,
    x: number,
    y: number,
    width: number,
    height: number,
  ) => {
    const scale = Math.min(width / image.naturalWidth, height / image.naturalHeight);
    const w = image.naturalWidth * scale;
    const h = image.naturalHeight * scale;
    context.drawImage(image, x + width - w, y + height - h, w, h);
  };
  contain(artwork.header, canvas.width - 390, 40, 360, 232);
  context.filter = studyNoteTone(0).brush;
  context.drawImage(artwork.highlight, LEFT - 4, 66, 220, 68);
  context.filter = 'none';
  context.font = `800 36px ${FONT_FAMILY}`;
  context.fillStyle = '#385339';
  context.fillText('课堂小记', LEFT + 24, 82);
  const titleEnd = drawLines(context, page.titleLines, 144);
  context.drawImage(artwork.highlight, LEFT, titleEnd + 2, TITLE_WIDTH, 14);
  drawLines(context, page.metadataLines, titleEnd + 22);
  const rule = (x1: number, y1: number, x2: number, y2: number, dashed = true) => {
    context.strokeStyle = '#E4D6BD';
    context.lineWidth = 1.5;
    context.setLineDash(dashed ? [3, 3] : []);
    context.beginPath();
    context.moveTo(x1, y1);
    context.lineTo(x2, y2);
    context.stroke();
    context.setLineDash([]);
  };
  let y = page.bodyTop;
  for (const panel of page.panels) {
    const tone = studyNoteTone(panel.section - 1);
    context.filter = tone.ring;
    context.drawImage(artwork.ring, LEFT, y, 72, 72);
    context.filter = 'none';
    context.font = `700 38px ${FONT_FAMILY}`;
    context.fillStyle = '#385339';
    const number = String(panel.section).padStart(2, '0');
    context.fillText(number, LEFT + 36 - context.measureText(number).width / 2, y + 18);
    let headingY = y;
    for (const line of panel.headingLines) {
      const width = line.pieces.reduce((sum, piece) => sum + piece.width, 0);
      context.filter = tone.brush;
      context.drawImage(
        artwork.highlight,
        LEFT + 84,
        headingY + line.height * 0.45,
        width + 34,
        46,
      );
      context.filter = 'none';
      rule(
        LEFT + 124 + width,
        headingY + line.height - 10,
        canvas.width - LEFT,
        headingY + line.height - 10,
      );
      headingY = drawLines(context, [line], headingY, LEFT + 96);
    }
    let lineY = y + panel.headingHeight;
    if (panel.columns) {
      const width = CONTENT_WIDTH / panel.lines.length;
      for (const [index, line] of panel.lines.entries()) {
        drawLines(context, [line], lineY, BODY_X + index * width);
        if (index > 0)
          rule(
            LEFT + index * width,
            lineY + 14,
            LEFT + index * width,
            y + panel.height - 14,
            false,
          );
      }
      rule(LEFT, y + panel.height, canvas.width - LEFT, y + panel.height);
    } else {
      for (const line of panel.lines) {
        lineY = drawLines(context, [line], lineY, BODY_X);
        rule(LEFT, lineY, canvas.width - LEFT, lineY);
      }
    }
    y += panel.height + SECTION_GAP;
  }
  context.drawImage(artwork.swoosh, LEFT, page.height - 94, 570, 64);
  contain(artwork.footer, canvas.width - LEFT - 400, page.height - 248, 400, 224);
  if (pageCount > 1) {
    context.font = `400 24px ${FONT_FAMILY}`;
    context.fillStyle = '#796c5d';
    context.fillText(`${pageNumber} / ${pageCount}`, LEFT, page.height - 50);
  }
}

export async function noteCanvasPng(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) =>
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('便签图片生成失败，请重试。'))),
      'image/png',
    ),
  );
}
export function saveNoteImage(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
